import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import { IComboBoxValue } from "../../../models/form";
import * as models from "../../../models";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as utils from "../../../utils/utils";
import * as apiBurialMethods from "../../../api/burialMethods";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";
import { CheckBox } from "../../../components/checkbox";
import ColorPicker from "../../../components/colorpicker";
import { LABEL_COLORS, LABEL_PATTERNS } from "../../../utils/constants";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    burialMethodId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    burialMethod?: modelsGet.IBurialMethod,

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        method?: string,
        method_short?: string
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see Dialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.burialMethodId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Betreff aus der Datenbank laden.
        var burialMethod: modelsGet.IBurialMethod = await apiBurialMethods.getBurialMethod(props.burialMethodId);

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.burialMethod = burialMethod;

            state.form.method = burialMethod.method;
            state.form.method_short = burialMethod.method_short;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        // Irgend etwas muss geladen werden, sonst funktioniert setState nicht.
        var dummy = await apiBurialMethods.getBurialMethod(0);

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;

            newState.form = {};

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.method !== nextState.form.method)
                this._isSaveButtonDisabled = false;

            if (form.method_short !== nextState.form.method_short)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, burialMethod, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.IBurialMethod = {};
            newData.method = form.method;
            newData.method_short = form.method_short;

            if (mode === Mode.edit)
            {
                await apiBurialMethods.put(burialMethod.id, newData);
            }
            else
            {
                await apiBurialMethods.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { burialMethod } = this.state;

            await apiBurialMethods.remove(burialMethod.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            burialMethod,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Bestattungsart"
                            value={form.method}
                            size={InputSize.normal}
                            autoFocus={true}
                            errorMessage={errors["Method"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "method")} />
                        <TextBox
                            label="Bestattungsart (kurze Schreibweise)"
                            value={form.method_short}
                            size={InputSize.normal}
                            errorMessage={errors["MethodShort"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "method_short")} />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            burialMethod,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Bestattungsart erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Bestattungsart "${burialMethod.method}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}